import React, { useState } from "react";
import {
    Trash2, ExternalLink, BookmarkPlus, ChefHat, UtensilsCrossed, Newspaper,
    ShoppingBag, Play, BookOpen, Bookmark as BookmarkIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";

// Category → { label, tone, Icon }. Kept separate from Glossary's palette so the two visual
// languages don't collide when a bookmark is later promoted to a glossary term.
const CATEGORY_META = {
    recipe:     { label: "RECIPE",     tone: "text-orange-300 border-orange-400/40 bg-orange-500/10",   Icon: ChefHat },
    restaurant: { label: "RESTAURANT", tone: "text-amber-300 border-amber-400/40 bg-amber-500/10",     Icon: UtensilsCrossed },
    article:    { label: "ARTICLE",    tone: "text-sky-300 border-sky-400/40 bg-sky-500/10",           Icon: Newspaper },
    product:    { label: "PRODUCT",    tone: "text-emerald-300 border-emerald-400/40 bg-emerald-500/10", Icon: ShoppingBag },
    video:      { label: "VIDEO",      tone: "text-red-300 border-red-400/40 bg-red-500/10",           Icon: Play },
    reference:  { label: "REFERENCE",  tone: "text-violet-300 border-violet-400/40 bg-violet-500/10",  Icon: BookOpen },
    other:      { label: "SAVED",      tone: "text-zinc-300 border-meye-border bg-zinc-800/30",        Icon: BookmarkIcon },
};

const DetailBlock = ({ details, category }) => {
    if (!details || typeof details !== "object") return null;
    const entries = Object.entries(details).filter(([, v]) => v !== null && v !== "" && !(Array.isArray(v) && v.length === 0));
    if (!entries.length) return null;

    // Recipe/restaurant have known ordered fields — everything else falls through to a generic list.
    const preferredOrder = {
        recipe: ["cuisine", "servings", "prep_time", "cook_time", "ingredients", "steps"],
        restaurant: ["cuisine", "address", "city", "price_range", "hours", "phone"],
        product: ["brand", "price", "rating", "key_features"],
        article: ["author", "published", "reading_time_min", "key_points"],
        video: ["channel", "duration", "topic"],
        reference: ["topic", "key_takeaways"],
    }[category] || null;

    const sorted = preferredOrder
        ? entries.sort((a, b) => {
            const ai = preferredOrder.indexOf(a[0]);
            const bi = preferredOrder.indexOf(b[0]);
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        })
        : entries;

    return (
        <dl className="mt-2 space-y-1.5 text-xs">
            {sorted.map(([k, v]) => {
                const label = k.replace(/_/g, " ");
                if (Array.isArray(v)) {
                    return (
                        <div key={k}>
                            <dt className="text-[10px] uppercase tracking-widest text-zinc-500 font-brand">{label}</dt>
                            <ul className="mt-0.5 list-disc list-inside text-zinc-300 space-y-0.5">
                                {v.slice(0, 10).map((item, i) => (
                                    <li key={i} className="marker:text-meye-cyan/60">{String(item)}</li>
                                ))}
                            </ul>
                        </div>
                    );
                }
                return (
                    <div key={k} className="flex gap-2">
                        <dt className="text-[10px] uppercase tracking-widest text-zinc-500 font-brand min-w-[80px]">{label}</dt>
                        <dd className="text-zinc-300 flex-1">{String(v)}</dd>
                    </div>
                );
            })}
        </dl>
    );
};

export const BookmarkCard = ({ bookmark, onDelete, onPromoted }) => {
    const meta = CATEGORY_META[bookmark.category] || CATEGORY_META.other;
    const { Icon } = meta;
    const [expanded, setExpanded] = useState(false);
    const [promoting, setPromoting] = useState(false);
    const hasDetails = bookmark.details && Object.keys(bookmark.details).length > 0;
    const promoted = !!bookmark.promoted_to_glossary_id;

    const promote = async () => {
        if (promoting || promoted) return;
        setPromoting(true);
        try {
            const res = await api.promoteBookmark(bookmark.id);
            toast.success(res.reused ? "already in glossary" : `saved "${res.glossary.term}" to glossary`);
            onPromoted?.({ ...bookmark, promoted_to_glossary_id: res.glossary.id });
        } catch (e) {
            toast.error("promote failed");
        } finally {
            setPromoting(false);
        }
    };

    return (
        <div
            className="bg-meye-card border border-meye-border rounded-md p-3.5 fade-up"
            data-testid={`bookmark-card-${bookmark.id}`}
        >
            <div className="flex items-start gap-3">
                <Icon size={16} className={`${meta.tone.split(" ")[0]} mt-0.5 flex-shrink-0`} strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${meta.tone}`}>
                            {meta.label}
                        </span>
                        {bookmark.site && (
                            <span className="text-[10px] font-mono text-zinc-500 truncate max-w-[180px]">{bookmark.site}</span>
                        )}
                    </div>

                    <h4 className="font-brand text-sm uppercase tracking-wider text-zinc-100 mt-1.5 break-words">
                        {bookmark.title}
                    </h4>

                    {bookmark.summary && (
                        <p className="text-xs text-zinc-400 mt-1 whitespace-pre-wrap break-words">{bookmark.summary}</p>
                    )}

                    {bookmark.tags?.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                            {bookmark.tags.map((t) => (
                                <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded-full border border-meye-border bg-black/40 text-zinc-400">
                                    #{t}
                                </span>
                            ))}
                        </div>
                    )}

                    {expanded && hasDetails && <DetailBlock details={bookmark.details} category={bookmark.category} />}

                    <div className="mt-2.5 flex items-center gap-3 text-[10px] uppercase tracking-widest font-mono flex-wrap">
                        <a
                            href={bookmark.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-meye-cyan hover:text-white inline-flex items-center gap-1"
                            data-testid={`bookmark-open-${bookmark.id}`}
                        >
                            <ExternalLink size={10} strokeWidth={2.5} /> open
                        </a>
                        {hasDetails && (
                            <button
                                onClick={() => setExpanded((v) => !v)}
                                className="text-zinc-500 hover:text-zinc-200"
                                data-testid={`bookmark-expand-${bookmark.id}`}
                            >
                                {expanded ? "less" : "details"}
                            </button>
                        )}
                        <button
                            onClick={promote}
                            disabled={promoting || promoted}
                            className={`inline-flex items-center gap-1 ${
                                promoted
                                    ? "text-emerald-400"
                                    : "text-zinc-500 hover:text-meye-fuchsia disabled:opacity-40"
                            }`}
                            data-testid={`bookmark-promote-${bookmark.id}`}
                        >
                            <BookmarkPlus size={10} strokeWidth={2.5} />
                            {promoted ? "in glossary" : promoting ? "saving..." : "save to glossary"}
                        </button>
                    </div>
                </div>

                <button
                    onClick={() => onDelete(bookmark.id)}
                    className="text-zinc-600 hover:text-red-400 flex-shrink-0"
                    data-testid={`bookmark-delete-${bookmark.id}`}
                    aria-label="Delete bookmark"
                >
                    <Trash2 size={13} strokeWidth={1.5} />
                </button>
            </div>
        </div>
    );
};

/** Inline "add via URL" row shown at the top of the bookmarks tab. Calls the smart-extract endpoint
 *  which classifies the page + persists the bookmark server-side. */
export const BookmarkAddForm = ({ onCreated }) => {
    const [url, setUrl] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e?.preventDefault?.();
        const clean = url.trim();
        if (!clean) return;
        if (!/^https?:\/\//i.test(clean)) {
            toast.error("URL must start with http:// or https://");
            return;
        }
        setLoading(true);
        try {
            const res = await api.extractBookmark({ url: clean });
            if (res.reused) toast.info("already saved — refreshed");
            else toast.success(`saved (${res.bookmark.category})`);
            onCreated?.(res.bookmark);
            setUrl("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "capture failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={submit} className="flex gap-2 mb-3" data-testid="bookmark-add-form">
            <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="paste a URL and let mEYE classify it..."
                className="flex-1 bg-meye-card border border-meye-border rounded px-3 py-2 text-xs font-mono text-zinc-100 focus:outline-none focus:border-meye-cyan"
                data-testid="bookmark-add-input"
                disabled={loading}
            />
            <button
                type="submit"
                disabled={loading || !url.trim()}
                className="px-3 py-2 rounded bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[11px] font-brand font-bold tracking-widest disabled:opacity-40"
                data-testid="bookmark-add-submit"
            >
                {loading ? "capturing..." : "capture"}
            </button>
        </form>
    );
};

export default BookmarkCard;
